import { BarChart3 } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Section } from '../features/menu/ui';
import { useToastLayout } from '../state/ToastProvider';
import { analyticsEnabled, getConsent, setConsent, subscribeConsent } from '../utils/analytics';
import { ToggleChip } from './ui/ToggleChip';

function useConsent() {
  return useSyncExternalStore(subscribeConsent, getConsent, () => 'unset' as const);
}

/** Kleine, niet-blokkerende vraag om anonieme statistieken. Verschijnt alleen als analytics aanstaat. */
export function AnalyticsConsent() {
  const consent = useConsent();
  const { bottomPx } = useToastLayout();
  if (!analyticsEnabled() || consent !== 'unset') return null;
  // Boven de timerbalk (zoals de gewone meldingen), onder dialogen en het menu.
  return createPortal(
    <div
      role="region"
      aria-label="Anonieme statistieken"
      className="pointer-events-none fixed inset-x-0 z-[110] flex justify-center px-4"
      style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomPx}px)` }}
    >
      <div className="pointer-events-auto w-full max-w-xl space-y-3 rounded-2xl bg-slate-900/95 p-4 text-sm text-slate-50 backdrop-blur-md dark:bg-slate-100/95 dark:text-slate-900">
        <p>Mag ik anoniem bijhouden hoe de app gebruikt wordt (bezoeken en duur)? Zo kan ik hem verbeteren.</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setConsent('granted')}
            className="min-h-11 whitespace-nowrap rounded-2xl bg-slate-50 px-4 font-semibold text-slate-900 transition-all duration-150 active:scale-95 dark:bg-slate-900 dark:text-slate-50"
          >
            Toestaan
          </button>
          <button
            type="button"
            onClick={() => setConsent('denied')}
            className="min-h-11 whitespace-nowrap rounded-2xl px-4 font-semibold transition-all duration-150 active:scale-95"
          >
            Niet nu
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Aan/uit in het menu, zodat een keuze altijd te wijzigen is. */
export function AnalyticsToggle() {
  const consent = useConsent();
  if (!analyticsEnabled()) return null;
  const on = consent === 'granted';
  return (
    <Section
      icon={BarChart3}
      title="Anonieme statistieken"
      intro="Anoniem bezoek en gebruiksduur, om de app te verbeteren. Je gegevens in de app blijven op je toestel."
    >
      <ToggleChip pressed={on} onToggle={() => setConsent(on ? 'denied' : 'granted')}>
        {on ? 'Statistieken aan' : 'Statistieken uit'}
      </ToggleChip>
    </Section>
  );
}
